import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { User } from '@sulis-liiga/common';
import userService from '../../services/userService';

// Define the state interface
interface UserState {
  profile: User | null;
  loading: boolean;
  error: string | null;
  updateSuccess: boolean;
}

// Initial state
const initialState: UserState = {
  profile: null,
  loading: false,
  error: null,
  updateSuccess: false,
};

// Async thunks
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const profile = await userService.getCurrentUserProfile();
      return profile;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch profile. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (
    profileData: {
      firstName?: string;
      lastName?: string;
      email?: string;
      profilePicture?: string;
      phone?: string;
      address?: {
        street?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
      };
      preferences?: {
        emailOnPendingAction?: boolean;
        autoApproveResults?: boolean;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const updatedProfile = await userService.updateProfile(profileData);
      return updatedProfile;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update profile. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

// Create the user slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserError: state => {
      state.error = null;
    },
    clearUpdateSuccess: state => {
      state.updateSuccess = false;
    },
    clearUserProfile: state => {
      state.profile = null;
      state.error = null;
      state.updateSuccess = false;
    },
  },
  extraReducers: builder => {
    // Fetch profile cases
    builder.addCase(fetchUserProfile.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.profile = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update profile cases
    builder.addCase(updateUserProfile.pending, state => {
      state.loading = true;
      state.error = null;
      state.updateSuccess = false;
    });
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.profile = action.payload;
      state.error = null;
      state.updateSuccess = true;
    });
    builder.addCase(updateUserProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.updateSuccess = false;
    });
  },
});

export const { clearUserError, clearUpdateSuccess, clearUserProfile } = userSlice.actions;
export default userSlice.reducer;
